// Sprint 0082 — Manpower allocation API client.
import apiClient from '@/shared/api/client'

// ── Sanctioned Strength ────────────────────────────────────────────────────

export interface SanctionedStrength {
  id: string
  plant_id: string
  department_id: string
  section_code: string | null
  machine_type: string | null
  designation_code: string | null
  worker_category: string
  shift_id: string | null
  sanctioned_count: number
  effective_from: string
  effective_to: string | null
  approval_status: 'PENDING' | 'APPROVED' | 'REJECTED'
  is_active: boolean
  remarks: string | null
}

export interface GapAnalysis {
  plant_id: string
  department_id: string
  as_of: string
  breakdown: Array<{
    worker_category: string
    sanctioned: number
    actual: number
    gap: number
    shortage: number
    excess: number
  }>
}

export const sanctionedApi = {
  list: async (params: {
    plant_id?: string; department_id?: string; as_of?: string
    only_approved?: boolean
  } = {}) =>
    (await apiClient.get<SanctionedStrength[]>('/hr/sanctioned-strength', { params })).data,
  create: async (body: Omit<SanctionedStrength, 'id' | 'approval_status' | 'is_active'>) =>
    (await apiClient.post<SanctionedStrength>('/hr/sanctioned-strength', body)).data,
  approve: async (id: string) =>
    (await apiClient.post<SanctionedStrength>(`/hr/sanctioned-strength/${id}/approve`)).data,
  patch: async (id: string, body: Partial<SanctionedStrength>) =>
    (await apiClient.patch<SanctionedStrength>(`/hr/sanctioned-strength/${id}`, body)).data,
  deactivate: async (id: string) =>
    (await apiClient.post<SanctionedStrength>(`/hr/sanctioned-strength/${id}/deactivate`)).data,
  endDate: async (id: string, on_date: string) =>
    (await apiClient.post<SanctionedStrength>(
      `/hr/sanctioned-strength/${id}/end-date`, null, { params: { on_date } }
    )).data,
  gapAnalysis: async (params: {
    plant_id: string; department_id: string; as_of: string
  }) =>
    (await apiClient.get<GapAnalysis>('/hr/sanctioned-strength/gap-analysis', { params })).data,
}

// ── Manpower Norms ─────────────────────────────────────────────────────────

export interface ManpowerNorm {
  id: string
  plant_id: string
  department_id: string
  section_code: string | null
  machine_id: string | null
  machine_type: string
  shift_id: string | null
  role_required: string
  required_skill_level: number
  required_count: string
  critical_post_flag: boolean
  certification_required: boolean
  effective_from: string
  effective_to: string | null
  is_active: boolean
  remarks: string | null
}

// HR-004 follow-up — workbench inline-edit patch. All fields optional;
// only those present are sent. Identity fields (plant/dept/machine) are
// not editable here — clone instead if you need to move a norm.
export interface NormPatchBody {
  section_code?: string | null
  machine_type?: string
  shift_id?: string | null
  role_required?: string
  required_skill_level?: number
  required_count?: number | string
  critical_post_flag?: boolean
  certification_required?: boolean
  effective_from?: string
  effective_to?: string | null
  remarks?: string | null
  is_active?: boolean
}

// HR-004 follow-up — Machine Groups (1 operator covers N machines).
// Used for Carding, Drawing, Combing, etc. where one Tenter covers the
// whole section. Ring Frame stays per-machine (one operator per machine).

export interface MachineGroup {
  id: string
  plant_id: string
  department_id: string
  group_code: string
  group_name: string
  machine_type: string
  role_code: string
  required_operator_count: number
  required_skill_level: number
  critical_post_flag: boolean
  certification_required: boolean
  shift_id: string | null
  effective_from: string
  effective_to: string | null
  is_active: boolean
  remarks: string | null
  machine_ids: string[]
}

export interface MachineGroupCreate {
  plant_id: string
  department_id: string
  group_code: string
  group_name: string
  machine_type: string
  machine_ids: string[]
  role_code?: string
  required_operator_count?: number
  required_skill_level?: number
  critical_post_flag?: boolean
  certification_required?: boolean
  shift_id?: string | null
  effective_from?: string
  effective_to?: string | null
  remarks?: string | null
}

export interface MachineGroupPatch {
  group_name?: string
  role_code?: string
  required_operator_count?: number
  required_skill_level?: number
  critical_post_flag?: boolean
  certification_required?: boolean
  shift_id?: string | null
  effective_from?: string
  effective_to?: string | null
  remarks?: string | null
  is_active?: boolean
}

export const machineGroupsApi = {
  list: async (params: {
    plant_id: string; department_id?: string;
    machine_type?: string; is_active?: boolean
  }) =>
    (await apiClient.get<MachineGroup[]>('/hr/machine-groups', { params })).data,
  get: async (id: string) =>
    (await apiClient.get<MachineGroup>(`/hr/machine-groups/${id}`)).data,
  create: async (body: MachineGroupCreate) =>
    (await apiClient.post<MachineGroup>('/hr/machine-groups', body)).data,
  patch: async (id: string, body: MachineGroupPatch) =>
    (await apiClient.patch<MachineGroup>(`/hr/machine-groups/${id}`, body)).data,
  setMembers: async (id: string, machine_ids: string[]) =>
    (await apiClient.put<MachineGroup>(`/hr/machine-groups/${id}/members`,
      { machine_ids })).data,
  remove: async (id: string) =>
    (await apiClient.delete(`/hr/machine-groups/${id}`)).data,
}


// HR-004 follow-up — Department Role Norms.
// Per-department floating role requirements. E.g. Ring Frame needs
// 3 GAITER + 1 SWEEPER + 1 RELIEVER per shift. Worker Allocation board
// reads these and renders 'expected floating seats' to show what's
// still unfilled.

export interface DeptRoleNorm {
  id: string
  plant_id: string
  department_id: string
  role_code: string
  required_count: number
  required_skill_level: number
  critical_post_flag: boolean
  certification_required: boolean
  shift_id: string | null
  effective_from: string
  effective_to: string | null
  is_active: boolean
  remarks: string | null
}

export interface DeptRoleNormCreate {
  plant_id: string
  department_id: string
  role_code: string
  required_count?: number
  required_skill_level?: number
  critical_post_flag?: boolean
  certification_required?: boolean
  shift_id?: string | null
  effective_from?: string
  effective_to?: string | null
  remarks?: string | null
}

export interface DeptRoleNormPatch {
  required_count?: number
  required_skill_level?: number
  critical_post_flag?: boolean
  certification_required?: boolean
  shift_id?: string | null
  effective_from?: string
  effective_to?: string | null
  is_active?: boolean
  remarks?: string | null
}

export const deptRoleNormsApi = {
  list: async (params: {
    plant_id: string; department_id?: string; role_code?: string;
    is_active?: boolean
  }) =>
    (await apiClient.get<DeptRoleNorm[]>('/hr/dept-role-norms', { params })).data,
  create: async (body: DeptRoleNormCreate) =>
    (await apiClient.post<DeptRoleNorm>('/hr/dept-role-norms', body)).data,
  patch: async (id: string, body: DeptRoleNormPatch) =>
    (await apiClient.patch<DeptRoleNorm>(`/hr/dept-role-norms/${id}`, body)).data,
  remove: async (id: string) =>
    (await apiClient.delete(`/hr/dept-role-norms/${id}`)).data,
}


// HR-004 follow-up — Worker Role Master.
// Admin-editable list of worker roles. Replaces the hardcoded
// WORKER_ROLES / FLOATING_ROLES constants. Frontend code merges
// the static defaults with the DB-driven list, so existing dropdowns
// keep working even if the API hasn't responded yet.

export interface WorkerRole {
  id: string
  plant_id: string | null
  role_code: string
  role_name: string
  role_type: 'MACHINE_BOUND' | 'FLOATING' | 'BOTH'
  color_tag: string
  description: string | null
  display_order: number
  is_active: boolean
  system_seeded: boolean
}

export interface WorkerRoleCreate {
  role_code: string
  role_name: string
  role_type?: 'MACHINE_BOUND' | 'FLOATING' | 'BOTH'
  color_tag?: string
  description?: string | null
  display_order?: number
  plant_id?: string | null
}

export interface WorkerRolePatch {
  role_name?: string
  role_type?: 'MACHINE_BOUND' | 'FLOATING' | 'BOTH'
  color_tag?: string
  description?: string | null
  display_order?: number
  is_active?: boolean
}

export const workerRolesApi = {
  list: async (params: {
    plant_id?: string; role_type?: string; is_active?: boolean
  } = {}) =>
    (await apiClient.get<WorkerRole[]>('/hr/worker-roles', { params })).data,
  create: async (body: WorkerRoleCreate) =>
    (await apiClient.post<WorkerRole>('/hr/worker-roles', body)).data,
  patch: async (id: string, body: WorkerRolePatch) =>
    (await apiClient.patch<WorkerRole>(`/hr/worker-roles/${id}`, body)).data,
  remove: async (id: string) =>
    (await apiClient.delete(`/hr/worker-roles/${id}`)).data,
}


// HR-004 follow-up — bulk cert lookup for the side allocation drawer.
// Returns one row per employee that HAS a skill matrix entry for the
// given machine_type. Employees not in the response have no entry —
// treat as NOT_CERTIFIED on the client.
export interface EmployeeCertification {
  employee_id: string
  certification_status: 'CERTIFIED' | 'NOT_CERTIFIED' | string
  skill_level: number
  certified_till: string | null
}

export const certificationsApi = {
  list: async (params: {
    machine_type: string; plant_id?: string; department_id?: string
  }) =>
    (await apiClient.get<EmployeeCertification[]>(
      '/hr/employee-certifications', { params })).data,
}

export const normsApi = {
  list: async (params: {
    plant_id?: string; department_id?: string;
    machine_id?: string; machine_type?: string;
    is_active?: boolean
  } = {}) =>
    (await apiClient.get<ManpowerNorm[]>('/hr/manpower-norms', { params })).data,
  create: async (body: Omit<ManpowerNorm, 'id' | 'is_active' | 'remarks'>) =>
    (await apiClient.post<ManpowerNorm>('/hr/manpower-norms', body)).data,
  // Inline-edit a norm. Only whitelisted fields are accepted server-side.
  patch: async (id: string, body: NormPatchBody) =>
    (await apiClient.patch<ManpowerNorm>(`/hr/manpower-norms/${id}`, body)).data,
  // Soft delete by default (is_active=false). Pass hard=true to physically
  // remove — only allowed when no allocation references the norm.
  remove: async (id: string, hard = false) =>
    (await apiClient.delete(`/hr/manpower-norms/${id}`, { params: { hard } })).data,
  // Convert a type-level norm into per-machine rows for every machine in
  // (department, machine_type). Optionally deactivates the source to
  // avoid double-counting on the allocation board.
  explode: async (id: string, deactivate_source = true) =>
    (await apiClient.post<ManpowerNorm[]>(
      `/hr/manpower-norms/${id}/explode`,
      null, { params: { deactivate_source } },
    )).data,
}

// ── Worker Allocation ──────────────────────────────────────────────────────

export interface WorkerAllocation {
  id: string
  allocation_date: string
  shift_id: string
  plant_id: string
  department_id: string
  section_code: string | null
  machine_id: string | null
  required_role: string
  required_skill_level: number
  required_count: string
  allocated_employee_id: string | null
  allocated_worker_category: string | null
  skill_level_at_allocation: number | null
  certification_status: string | null
  allocation_ratio: string | null
  backup_used: boolean
  shortage_flag: boolean
  unauthorized_deployment_flag: boolean
  allocation_status: 'DRAFT' | 'ALLOCATED' | 'VERIFIED' | 'LOCKED'
  verified_at: string | null
  locked_at: string | null
  // Phase HR-004 Wave 1 — Ring Frame sides (legacy aggregate fields)
  assigned_sides?: number | null
  additional_machine_ids?: string[] | null
  extra_per_side_amount?: string | null
  rate_variant?: 'NORMAL' | 'THREE_SIDE' | 'FOUR_SIDE' | 'OTHER' | null
  // Phase HR-004 Wave 1.5 — side-level allocation
  side_label?: 'A' | 'B' | null
  side_group_id?: string | null
  // HR-004 follow-up — group-level allocation
  machine_group_id?: string | null
}

// Side-level allocation request — one operator → 1..4 (machine, side) cells.
// Used for Ring Frame only.
export interface SideAssignment {
  machine_id: string
  side_label: 'A' | 'B'
}

export interface AllocateSidesBody {
  allocation_date: string
  shift_id: string
  plant_id: string
  department_id: string
  allocated_employee_id: string
  required_role?: string  // defaults to OPERATOR
  machine_type?: string   // defaults to RING_FRAME
  side_assignments: SideAssignment[]
  required_skill_level?: number
  critical_post_flag?: boolean
  backup_used?: boolean
  backup_reason?: string | null
  override_reason?: string | null
  override_authorised_by?: string | null
  extra_per_side_amount?: number | null
  source_manpower_norm_id?: string | null
  // Sprint 0144 ATT — reason when worker has no gate inward PunchLog
  no_gate_log_reason?: string | null
}

export interface AllocationCreateBody {
  allocation_date: string
  shift_id: string
  plant_id: string
  department_id: string
  required_role: string
  required_count?: string | number
  machine_id?: string | null
  section_code?: string | null
  allocated_employee_id?: string | null
  required_skill_level?: number
  critical_post_flag?: boolean
  backup_used?: boolean
  backup_reason?: string | null
  override_reason?: string | null
  override_authorised_by?: string | null
  // Phase HR-004 Wave 1 — Ring Frame sides
  assigned_sides?: number | null
  additional_machine_ids?: string[] | null
  extra_per_side_amount?: number | null
  // HR-004 follow-up — group-level allocation
  machine_group_id?: string | null
  // Sprint 0144 ATT — reason when worker has no gate inward PunchLog
  no_gate_log_reason?: string | null
}

export const allocationsApi = {
  list: async (params: {
    allocation_date?: string; shift_id?: string; machine_id?: string
    department_id?: string; employee_id?: string
  } = {}) =>
    (await apiClient.get<WorkerAllocation[]>('/hr/worker-allocations', { params })).data,
  create: async (body: AllocationCreateBody) =>
    (await apiClient.post<WorkerAllocation>('/hr/worker-allocations', body)).data,
  // Phase HR-004 Wave 1.5 — allocate one operator across 1..4 sides at once.
  // Backend validates adjacency + side conflicts; returns the new rows.
  allocateSides: async (body: AllocateSidesBody) =>
    (await apiClient.post<WorkerAllocation[]>('/hr/worker-allocations/sides', body)).data,
  verify: async (id: string) =>
    (await apiClient.post<WorkerAllocation>(`/hr/worker-allocations/${id}/verify`)).data,
  lock: async (id: string) =>
    (await apiClient.post<WorkerAllocation>(`/hr/worker-allocations/${id}/lock`)).data,
  byMachine: async (params: {
    machine_id: string; on_date: string; shift_id: string
  }) =>
    (await apiClient.get<WorkerAllocation[]>('/hr/worker-allocations/by-machine', { params })).data,

  // ── Sprint 0144 ATT — GM approval for no-gate-log overrides ───────────
  pendingGMApprovals: async (plantId: string) =>
    (await apiClient.get<PendingGMApproval[]>(
      '/hr/worker-allocations/pending-gm-approvals',
      { params: { plant_id: plantId } },
    )).data,
  gmApprove: async (allocId: string) =>
    (await apiClient.post<{ allocation_id: string; attendance_id: string; md_escalated: boolean }>(
      `/hr/worker-allocations/${allocId}/gm-approve`,
    )).data,
  gmReject: async (allocId: string, reason: string) =>
    (await apiClient.post<{ allocation_id: string; status: string }>(
      `/hr/worker-allocations/${allocId}/gm-reject`,
      { reason },
    )).data,
}

// Sprint 0144 ATT — row shape returned by /pending-gm-approvals.
// Mirrors attendance_auto_mark_service.list_pending_gm_approvals.
export interface PendingGMApproval {
  allocation_id: string
  allocation_date: string         // ISO date
  emp_code: string | null
  emp_name: string | null
  department_code: string | null
  department_name: string | null
  shift_id: string
  machine_id: string | null
  required_role: string
  no_gate_log_reason: string | null
  supervisor_id: string | null
  rolling_30d_override_count: number
  md_threshold_breached: boolean  // true when count > 3
  created_at: string | null
}

// ── Punch + Productivity ───────────────────────────────────────────────────

export interface PunchFeedRow {
  id: string
  punch_time: string
  direction: 'IN' | 'OUT' | 'UNKNOWN' | string
  location: string | null
  device_id: string | null
  source: string
  employee_id: string | null
  emp_code: string | null
  emp_name: string | null
  biometric_id: string | null
  department_name: string | null
}

export const punchesApi = {
  post: async (body: {
    employee_id: string; punch_time: string; direction?: 'IN' | 'OUT' | 'UNKNOWN'
    device_id?: string; location?: string; source?: string
  }) => (await apiClient.post('/hr/punches', body)).data,
  list: async (employeeId: string, params: { start: string; end?: string }) =>
    (await apiClient.get(`/hr/punches/${employeeId}`, { params })).data,
  dutyHours: async (employeeId: string, params: {
    on_date: string; roster_hours?: number
  }) => (await apiClient.get<{
    employee_id: string; date: string; punches_count: number
    actual_hours: number; roster_hours: number; variance_hours: number
    roster_source?: string
  }>(`/hr/punches/${employeeId}/duty-hours`, { params })).data,
  // Cross-employee feed: every punch on a date, denormalised with
  // emp_code, emp_name, biometric_id, department_name inline.
  feed: async (params: {
    on_date: string
    department_id?: string
    direction?: 'IN' | 'OUT' | 'UNKNOWN'
    limit?: number
  }) => (await apiClient.get<PunchFeedRow[]>('/hr/punches-feed', { params })).data,
}

// ── Daily Attendance Detailed Report (eSSL Excel-compatible format) ─────

export interface DailyAttendanceRow {
  sno: number
  emp_code: string
  biometric_id: string | null
  name: string
  department_name: string
  joining_employee_type: string | null
  shift_code: string
  s_in_time: string         // scheduled in HH:MM
  s_out_time: string        // scheduled out HH:MM
  a_in_time: string         // actual in HH:MM
  a_out_time: string        // actual out HH:MM
  work_duration: string     // HH:MM
  overtime: string          // HH:MM
  total_duration: string    // HH:MM
  late_by: string           // HH:MM
  early_going_by: string    // HH:MM
  status: string            // "Present" | "Absent" | "Half Day" | "...  *"
  anomaly_reason: string    // empty when no asterisk; otherwise human-readable
                            // e.g. "Double IN at 14:30 (no OUT between)"
  punch_records: string     // comma-separated "HH:MM:dir(device)" string
  punches_count: number
  work_sec: number
  ot_sec: number
  late_sec: number
  early_sec: number
}

export const dailyAttendanceReportApi = {
  fetch: async (params: {
    on_date: string
    department_id?: string
    employee_id?: string
    only_present?: boolean
  }) =>
    (await apiClient.get<{
      on_date: string; row_count: number; rows: DailyAttendanceRow[]
    }>('/hr/daily-attendance-report', { params })).data,
  // Returns the Excel binary; caller saves as a file
  downloadXlsxUrl: (params: {
    on_date: string
    department_id?: string
    employee_id?: string
    only_present?: boolean
  }) => {
    const q = new URLSearchParams()
    q.append('on_date', params.on_date)
    if (params.department_id) q.append('department_id', params.department_id)
    if (params.employee_id) q.append('employee_id', params.employee_id)
    if (params.only_present) q.append('only_present', 'true')
    return `/api/v1/hr/daily-attendance-report.xlsx?${q.toString()}`
  },
}

export const productivityApi = {
  labour: async (params: {
    department_id: string; on_date: string; shift_id?: string
  }) => (await apiClient.get<{
    department_id: string; date: string; production_kg: number
    total_labour_hours: number; worker_count: number; punch_based_workers: number
    kg_per_labour_hour: number
  }>('/hr/labour-productivity', { params })).data,
  workerEfficiency: async (employeeId: string, params: {
    on_date: string; shift_id?: string; standard_target_kg?: number
  }) => (await apiClient.get(`/hr/worker-efficiency/${employeeId}`, { params })).data,
}

// Common constants
export const WORKER_CATEGORIES = ['PERMANENT', 'CONTRACT', 'TRAINEE', 'STAFF', 'APPRENTICE'] as const
export const WORKER_ROLES = [
  'OPERATOR', 'HELPER', 'JOBBER', 'FITTER', 'ELECTRICIAN',
  'ELECTRONIC_TECHNICIAN', 'UTILITY_OPERATOR', 'SUPERVISOR', 'SHIFT_INCHARGE',
  // Phase HR-004 Wave 1 — Ring Frame specific (+ adjacent dept-wide roles)
  'GAITER', 'SWEEPER', 'RELIEVER',
  // HR-004 follow-up — additional shop-floor roles
  'DOFFER', 'PIECER', 'CAN_MAN', 'BOBBIN_BOY', 'BUNDLE_MAN',
  'OILER', 'CLEANER',
]

// Phase HR-004 Wave 1 — which roles operate ON a specific machine vs roam
// the whole department. Drives UI: machine + sides shown only for machine
// roles, hidden for department-wide roles.
//
// GAITER is floating — 1 per 4 Ring Frame machines, helps with idle,
// breakage, doff piecing across the line. Not pinned to one machine.
export const MACHINE_BOUND_ROLES = new Set([
  'OPERATOR', 'HELPER',
])

// Roles that are department-wide (roving / floating). The supervisor
// allocates these without picking a machine.
// Grouped by function for the picker dropdown.
export const FLOATING_ROLES = [
  // Ring Frame / spinning-area roles
  'GAITER',
  'DOFFER',          // Removes full bobbins, replaces with empty
  'PIECER',          // Repairs broken ends / piecing
  'JOBBER',          // Senior operator, dept supervisor support
  'RELIEVER',        // Skilled, substitutes for any role on break
  'SWEEPER',         // Floor cleaning during shift
  // Material movement / handling
  'CAN_MAN',         // Moves sliver cans between sections
  'BOBBIN_BOY',      // Carries empty / full bobbins
  'BUNDLE_MAN',      // Wraps and stacks output
  'HELPER',          // Generic dept helper
  // Maintenance roles (typically roving across many depts)
  'OILER',           // Daily lubrication round
  'CLEANER',         // Deep machine cleaning (vs. floor sweeping)
  'FITTER',          // Mechanical maintenance
  'ELECTRICIAN',     // Electrical maintenance
  'ELECTRONIC_TECHNICIAN',
  // Utilities
  'UTILITY_OPERATOR',
  // Supervision
  'SHIFT_INCHARGE',
  'SUPERVISOR',
] as const

// Phase HR-004 Wave 1 — Ring Frame side options.
// 1 machine = 2 sides. Default 2 (1 operator per machine). 3-side =
// operator covers 1 full machine + 1 side of next (shortage). 4-side =
// 2 full machines (rare, big shortage).
export const SIDE_OPTIONS = [2, 3, 4] as const
export type Sides = (typeof SIDE_OPTIONS)[number]
export const RATE_VARIANT_LABEL: Record<string, string> = {
  NORMAL: 'Normal (2 sides)',
  THREE_SIDE: 'Three-side shortage rate',
  FOUR_SIDE: 'Four-side shortage rate',
  OTHER: 'Other',
}
export const MACHINE_TYPES = [
  'BLOW_ROOM', 'CARDING', 'COMBER', 'DRAW_FRAME_BREAKER', 'DRAW_FRAME_FINISHER',
  'LAP_FORMER', 'SIMPLEX', 'RING_FRAME', 'WINDING', 'AUTO_CONER',
  'TFO', 'PACKING', 'HUMIDIFICATION', 'BOILER', 'YCP', 'WCS',
]
